import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { SteamApiService } from './steam-api.service';
import {
  SteamBoilerDaily,
  SteamBoilersSection,
  SteamDaily,
  SteamDailySection,
  SteamDowntime,
  SteamDowntimeSection,
  SteamMonthHeadline,
  SteamMonthly,
  SteamPlantReport,
  SteamSection,
  SteamSummarySection,
  SteamTab,
} from './steam.models';

/**
 * Headlines load once. Each tab fetches only that month's slice and then stays in memory.
 */
@Injectable({ providedIn: 'root' })
export class SteamStore {
  plantName = '';
  months: SteamMonthHeadline[] = [];
  unavailable = false;
  revision = 0;
  readonly revision$ = new BehaviorSubject(0);
  private reports = new Map<string, SteamPlantReport>();
  private attemptedPanes = new Set<string>();
  private readyPanes = new Set<string>();
  private loaded = false;
  private inflight: Observable<void> | null = null;
  private paneInflight = new Map<string, Observable<void>>();

  constructor(private api: SteamApiService) {}

  ensureLoaded(): Observable<void> {
    if (this.loaded) return of(undefined);
    if (this.inflight) return this.inflight;

    this.inflight = this.api.getOverview().pipe(
      tap(overview => {
        if (!overview?.plant) {
          this.unavailable = true;
        } else {
          this.unavailable = false;
          this.plantName = overview.plant.name ?? '';
          this.months = overview.months ?? [];
        }
        this.loaded = true;
        this.bump();
      }),
      map(() => undefined),
      catchError((err: { status?: number }) => {
        if (err?.status === 404) this.unavailable = true;
        this.loaded = true;
        this.bump();
        return of(undefined);
      }),
      finalize(() => {
        this.inflight = null;
      }),
      shareReplay(1),
    );
    return this.inflight;
  }

  ensurePane(billingMonth: string, tab: SteamTab): Observable<void> {
    const key = this.paneKey(billingMonth, tab);
    if (this.attemptedPanes.has(key)) return of(undefined);
    const existing = this.paneInflight.get(key);
    if (existing) return existing;

    const req: Observable<void> = this.api.getSection(billingMonth, tab).pipe(
      tap((section: SteamSection | null) => {
        if (section) {
          this.mergeSection(billingMonth, tab, section);
          this.readyPanes.add(key);
        }
        this.attemptedPanes.add(key);
        this.bump();
      }),
      catchError(() => {
        this.attemptedPanes.add(key);
        this.bump();
        return of(undefined);
      }),
      map(() => undefined),
      finalize(() => this.paneInflight.delete(key)),
      shareReplay(1),
    );
    this.paneInflight.set(key, req);
    return req;
  }

  hasPane(billingMonth: string | null | undefined, tab: SteamTab): boolean {
    if (!billingMonth) return false;
    return this.readyPanes.has(this.paneKey(billingMonth, tab));
  }

  isPaneLoading(billingMonth: string | null | undefined, tab: SteamTab): boolean {
    if (!billingMonth) return false;
    return this.paneInflight.has(this.paneKey(billingMonth, tab));
  }

  report(billingMonth: string | null | undefined): SteamPlantReport | null {
    if (!billingMonth) return null;
    return this.reports.get(billingMonth) ?? null;
  }

  private paneKey(billingMonth: string, tab: SteamTab): string {
    return `${billingMonth}:${tab}`;
  }

  private mergeSection(
    billingMonth: string,
    tab: SteamTab,
    section: SteamSection,
  ): void {
    const report = this.reports.get(billingMonth) ?? this.emptyReport(billingMonth, section.period, section.n_days);
    report.period = section.period;
    report.n_days = section.n_days;
    report.billingMonth = billingMonth;
    if (tab === 'summary') {
      report.monthly = (section as SteamSummarySection).monthly;
    } else if (tab === 'daily') {
      this.applyDaily(report, section as SteamDailySection);
    } else if (tab === 'boilers') {
      this.applyBoilers(report, section as SteamBoilersSection);
    } else {
      this.applyDowntime(report, section as SteamDowntimeSection);
    }
    this.reports.set(billingMonth, report);
  }

  private applyDaily(report: SteamPlantReport, section: SteamDailySection): void {
    report.daily = {
      ...report.daily,
      gas_purchased_mcf: section.gas_purchased_mcf,
      total_plant_steam_lbs: section.total_plant_steam_lbs,
      plant_load_min: section.plant_load_min,
      plant_load_max: section.plant_load_max,
      makeup_water_gal: section.makeup_water_gal,
      outdoor_temp_f: section.outdoor_temp_f,
      cost_gas: section.cost_gas,
      cost_oil: section.cost_oil,
      cost_total: section.cost_total,
      cdd: section.cdd,
      hdd: section.hdd,
    };
  }

  private applyBoilers(report: SteamPlantReport, section: SteamBoilersSection): void {
    report.monthly = {
      ...report.monthly,
      steam_by_boiler_gas: section.steam_by_boiler_gas,
      gas_by_boiler_mcf: section.gas_by_boiler_mcf,
      hours_online_gas_by_boiler: section.hours_online_gas_by_boiler,
      efficiency_gas_by_boiler: section.efficiency_gas_by_boiler,
    };
    report.daily = {
      ...report.daily,
      boiler1: section.boiler1,
      boiler2: section.boiler2,
      boiler3: section.boiler3,
      boiler4: section.boiler4,
      ro_gallons: section.ro_gallons,
      dealk1_gallons: section.dealk1_gallons,
      dealk2_gallons: section.dealk2_gallons,
    };
  }

  private applyDowntime(report: SteamPlantReport, section: SteamDowntimeSection): void {
    report.downtime = section.downtime;
    const hours = section.hours_online ?? [];
    report.daily.boiler1 = { ...report.daily.boiler1, hours: hours[0] ?? report.daily.boiler1.hours };
    report.daily.boiler2 = { ...report.daily.boiler2, hours: hours[1] ?? report.daily.boiler2.hours };
    report.daily.boiler3 = { ...report.daily.boiler3, hours: hours[2] ?? report.daily.boiler3.hours };
    report.daily.boiler4 = { ...report.daily.boiler4, hours: hours[3] ?? report.daily.boiler4.hours };
  }

  private emptyReport(billingMonth: string, period: string, nDays: number): SteamPlantReport {
    const zeros = Array.from({ length: nDays }, () => 0);
    const blanks = Array.from({ length: nDays }, () => null);
    const boiler = (): SteamBoilerDaily => ({
      hours: [...blanks],
      gas_mcf: [...blanks],
      steam_lbs: [...blanks],
      efficiency: [...blanks],
      load_min: [...blanks],
      load_max: [...blanks],
    });
    const daily: SteamDaily = {
      gas_purchased_mcf: [...zeros],
      total_plant_steam_lbs: [...zeros],
      plant_load_min: [...zeros],
      plant_load_max: [...zeros],
      makeup_water_gal: [...zeros],
      outdoor_temp_f: [...zeros],
      ro_start: [...zeros],
      ro_end: [...zeros],
      ro_gallons: [...zeros],
      dealk1_start: [...zeros],
      dealk1_end: [...zeros],
      dealk1_gallons: [...zeros],
      dealk2_start: [...zeros],
      dealk2_end: [...zeros],
      dealk2_gallons: [...zeros],
      boiler1: boiler(),
      boiler2: boiler(),
      boiler3: boiler(),
      boiler4: boiler(),
      cost_gas: [...zeros],
      cost_oil: [...zeros],
      cost_total: [...zeros],
      cdd: [...zeros],
      hdd: [...zeros],
    };
    const downtime: SteamDowntime = {
      boiler1_hours: [...zeros],
      boiler1_reason: [...blanks],
      boiler2_hours: [...zeros],
      boiler2_reason: [...blanks],
      boiler3_hours: [...zeros],
      boiler3_reason: [...blanks],
      boiler4_hours: [...zeros],
      boiler4_reason: [...blanks],
      boilers_active: [...zeros],
      overall_downtime: [...zeros],
      monthly_totals: { boiler1: 0, boiler2: 0, boiler3: 0, boiler4: 0, overall: 0 },
      reason_vocabulary: [],
    };
    return {
      facility: this.plantName,
      period,
      n_days: nDays,
      billingMonth,
      daily,
      downtime,
      monthly: {} as SteamMonthly,
    };
  }

  private bump(): void {
    this.revision += 1;
    this.revision$.next(this.revision);
  }
}
